import { API_BASE, handleResponse } from '../api';
import type { RawTouch } from '../acquisition';

/**
 * Shared auth API endpoints used by both student and teacher apps.
 *
 * These are stateless HTTP calls — they don't read or write localStorage.
 * Each app is responsible for persisting the token/user returned by login calls.
 */

export type OtpPurpose = 'register' | 'reset-password' | 'login';

/**
 * Which product an account created here belongs to.
 *
 * Recorded once, when the account is created; signing in to an existing one
 * leaves it alone. The science site has always sent its own value, so before
 * this the field could only ever say `ScienceXP` or nothing — and "nothing"
 * meant *either* "made on BuzzBrain" *or* "pre-dates the field", which is
 * exactly the ambiguity it exists to remove. See `SignUpForSchema`.
 */
const SIGN_UP_FOR = 'BuzzBrain';

/** Identity channel for the OTP / login flows. See docs/FeatureDesigns/phone-otp-login-target.md. */
export type AuthChannel = 'email' | 'phone';

/**
 * How the second sign-in screen should challenge an identifier.
 * Mirrors `IdentifyMethod` in `functions/src/auth/identify.ts`.
 */
export type IdentifyMethod = 'password' | 'otp' | 'google' | 'apple' | 'register' | 'unknown';

export interface IdentifyResult {
  /** Every challenge this identifier accepts, best first. */
  methods: IdentifyMethod[];
  otpChannel?: 'phone';
  /** The identifier as the server read it. Send this on, not what was typed. */
  normalized: string;
  /** A number outside India, which OTP delivery does not cover. */
  unreachable?: 'country-not-supported';
}

/**
 * POST /auth/identify — what does this identifier accept?
 *
 * Screen 1 asks for one thing and screen 2 has to ask for exactly one more, so
 * something has to decide which. Only the server can: whether the account has a
 * password, was created through Google, or exists at all.
 *
 * Never throws. A failure answers `unknown`, which routes to the password form
 * — the screen that works for most accounts and gives a real error if it
 * doesn't. A sign-in must not be blocked by a lookup.
 */
export async function identify(identifier: string): Promise<IdentifyResult> {
  try {
    const response = await fetch(`${API_BASE}/auth/identify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identifier }),
    });
    return await handleResponse<IdentifyResult>(response);
  } catch {
    return { methods: ['unknown'], normalized: identifier.trim().toLowerCase() };
  }
}

/** POST /auth/send-otp — sends a verification code to the user's email or phone.
 *  `channel` defaults to 'email' for back-compat with existing callers. */
export async function sendOtp(identifier: string, purpose: OtpPurpose, channel: AuthChannel = 'email'): Promise<void> {
  const response = await fetch(`${API_BASE}/auth/send-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ channel, identifier, purpose }),
  });
  await handleResponse(response);
}

/** POST /auth/verify-otp — exchanges a code for a short-lived verification token. */
export async function verifyOtp(identifier: string, code: string, purpose: OtpPurpose, channel: AuthChannel = 'email'): Promise<string> {
  const response = await fetch(`${API_BASE}/auth/verify-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ channel, identifier, code, purpose }),
  });
  const data = await handleResponse<{ verificationToken: string }>(response);
  return data.verificationToken;
}

/** POST /auth/reset-password — changes the password using the verification token. */
export async function resetPassword(verificationToken: string, newPassword: string): Promise<void> {
  const response = await fetch(`${API_BASE}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ verificationToken, newPassword }),
  });
  await handleResponse(response);
}

/** Persona an app registers a user as. `admin` requires a pending admin invite
 *  (see backend register.ts) — the admin console uses it; students/teachers use
 *  their own persona. */
export type RegisterRole = 'student' | 'teacher' | 'admin';

/** POST /auth/register — creates the account (or merges into an invite) after
 *  OTP verification. Stateless: returns the auth token + user for the caller to
 *  persist. Identifier is email OR phone (the verificationToken decides which);
 *  password is optional for phone signups. `U` is the app's user shape. */
export async function register<U = unknown>(params: {
  name: string;
  email?: string;
  phone?: string;
  password?: string;
  verificationToken: string;
  role?: RegisterRole;
}): Promise<{ token: string; user: U }> {
  const response = await fetch(`${API_BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...params, signUpFor: SIGN_UP_FOR }),
  });
  return handleResponse<{ token: string; user: U }>(response);
}

/** POST /auth/otp-login — passwordless sign-in for a verified phone. Exchanges a
 *  `login`-purpose verification token for the auth token + user. */
export async function otpLogin<U = unknown>(verificationToken: string): Promise<{ token: string; user: U; teacher?: unknown }> {
  const response = await fetch(`${API_BASE}/auth/otp-login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ verificationToken }),
  });
  return handleResponse<{ token: string; user: U; teacher?: unknown }>(response);
}

/** GET /auth/phone-login-available — pre-auth read of the phone-login
 *  kill-switch. Returns false on any error so the UI fails closed (hidden). */
export async function phoneLoginAvailable(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/auth/phone-login-available`, { method: 'GET' });
    const data = await handleResponse<{ enabled: boolean }>(response);
    return data.enabled === true;
  } catch {
    return false;
  }
}

/** POST /auth/google — exchanges a Google ID token for an app auth token. Returns raw JSON payload.
 *  `guestToken` is the guest this browser already is, when it has one: sent as
 *  the bearer, the way `register` does, so the backend promotes that guest in
 *  place rather than minting a second account beside it. `touch` is where the
 *  browser came from (@shared-ui/acquisition), for an account this creates. */
export async function googleSignIn<T>(idToken: string, guestToken?: string | null, touch?: RawTouch): Promise<T> {
  const response = await fetch(`${API_BASE}/auth/google`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(guestToken ? { Authorization: `Bearer ${guestToken}` } : {}),
    },
    body: JSON.stringify({ idToken, clientType: 'web', signUpFor: SIGN_UP_FOR, ...(touch ? { touch } : {}) }),
  });
  return handleResponse<T>(response);
}
